import { useSalaryHistogram } from '../histogram/use-salary-histogram'
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { DropdownMenu } from '@/components/ui/dropdown-menu'
import { ChevronDown } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/css-utils'
import { SortDescending } from '@/components/icons/sort-descending'
import { SortAscending } from '@/components/icons/sort-ascending'
import { useSalaryFilters } from '../filters/use-salary-filters'
import { SortOption } from '@/modules/salary/constants'
import { useIsMobile } from '@/utils/screen'
import {
  BottomSheet,
  BottomSheetContent,
  BottomSheetTrigger,
} from '@/components/ui/bottom-sheet'
import { Button } from '@/components/ui/deprecated-button'
import { SelectItem } from '@/components/ui/select-item'
import { usePostHog } from 'posthog-js/react'
import { getFormattedSalaryCount } from '../common/get-formatted-salary-count'
import LoadingSpinner from '@/components/ui/loading-spinner'
import { LoadingResults } from '../salary-table-context'
import { SalaryTabs } from '../trends/salary-tabs'
import { hasTrendsTab } from '../trends/utils'

function SortIconAscending() {
  return <SortAscending className="dark:text-blue-50 text-blue-40" />
}

function SortIconDescending() {
  return <SortDescending className="dark:text-blue-50 text-blue-40" />
}

const sortOptions: Array<
  Array<{
    value: SortOption
    label: string
    shortLabel?: string
    Icon: (props: React.ComponentProps<'svg'>) => React.ReactNode
  }>
> = [
  [
    {
      value: SortOption.postedAtDesc,
      label: 'Date',
      Icon: SortIconDescending,
    },
    {
      value: SortOption.postedAtAsc,
      label: 'Date',
      Icon: SortIconAscending,
    },
  ],
  [
    {
      value: SortOption.totalCompDesc,
      label: 'Total Comp',
      Icon: SortIconDescending,
    },
    {
      value: SortOption.totalCompAsc,
      label: 'Total Comp',
      Icon: SortIconAscending,
    },
  ],
  [
    {
      value: SortOption.yearsOfExperienceDesc,
      label: 'Years of Experience',
      shortLabel: 'YOE',
      Icon: SortIconDescending,
    },
    {
      value: SortOption.yearsOfExperienceAsc,
      label: 'Years of Experience',
      shortLabel: 'YOE',
      Icon: SortIconAscending,
    },
  ],
  [
    {
      value: SortOption.avgHoursPerWeekDesc,
      label: 'Average Hours Worked',
      shortLabel: 'Hrs Worked',
      Icon: SortIconDescending,
    },
    {
      value: SortOption.avgHoursPerWeekAsc,
      label: 'Average Hours Worked',
      shortLabel: 'Hrs Worked',
      Icon: SortIconAscending,
    },
  ],
]

function SortByTrigger({
  sortBy,
  isLoading,
}: {
  sortBy: SortOption | null
  isLoading: boolean
}) {
  const activeOption = sortOptions.find((optionGroup) =>
    optionGroup.some((option) => sortBy === option.value),
  )?.[0]
  return (
    <span className="flex items-center text-sm gap-1">
      <span className="text-sm text-copy-accent font-normal">Sort by</span>
      <span className="text-sm text-copy-primary font-medium">
        <span className="hidden md:block">{activeOption?.label}</span>
        <span className="block md:hidden">{activeOption?.shortLabel}</span>
      </span>
      {isLoading ? (
        <LoadingSpinner size="small" className="text-copy-primary" />
      ) : (
        <ChevronDown size={20} className="text-copy-primary" />
      )}
    </span>
  )
}

function getNextOption(sortBy: SortOption | null, option: SortOption) {
  if (!sortBy) {
    return option
  }
  const isCurrentOption = sortBy === option
  if (!isCurrentOption) {
    return option
  }
  const currentOptionGroupIndex = sortOptions.findIndex((optionGroup) =>
    optionGroup.some((option) => sortBy === option.value),
  )
  const currentOptionIndex = sortOptions[currentOptionGroupIndex].findIndex(
    (option) => sortBy === option.value,
  )
  const nextOptionIndex =
    (currentOptionIndex + 1) % sortOptions[currentOptionGroupIndex].length
  return sortOptions[currentOptionGroupIndex][nextOptionIndex].value
}

function SortRowDesktop({
  sortBy,
  setSortBy,
  isLoading,
}: {
  sortBy: SortOption | null
  setSortBy: (option: SortOption | null) => void
  isLoading: boolean
}) {
  const handleSortChange = (option: SortOption) => {
    setSortBy(getNextOption(sortBy, option))
  }

  return (
    <div className="flex items-center gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm">
            <SortByTrigger sortBy={sortBy} isLoading={isLoading} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className={cn(
            'min-w-40 bg-background-primary border-border-muted shadow-md',
          )}
        >
          {sortOptions.map((toggleOptionGroup) => {
            const activeOption =
              toggleOptionGroup.find((option) => sortBy === option.value) ||
              toggleOptionGroup[0]
            const { Icon, value, label } = activeOption
            return (
              <DropdownMenuItem
                key={value}
                isActive={sortBy === value}
                onClick={() => handleSortChange(value)}
                className="justify-between"
              >
                {label} {sortBy === activeOption.value ? <Icon /> : null}
              </DropdownMenuItem>
            )
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

function SortRowMobile({
  sortBy,
  setSortBy,
  isLoading,
}: {
  sortBy: SortOption | null
  setSortBy: (option: SortOption | null) => void
  isLoading: boolean
}) {
  const [open, setOpen] = useState(false)
  const [activeSortBy, setActiveSortBy] = useState<SortOption | null>(null)
  const handleItemClick = (option: SortOption) => {
    setActiveSortBy(getNextOption(activeSortBy, option))
  }

  useEffect(() => {
    setActiveSortBy(sortBy)
  }, [sortBy])

  return (
    <BottomSheet open={open} onOpenChange={setOpen}>
      <BottomSheetTrigger asChild>
        <Button variant="ghost" size="sm">
          <SortByTrigger sortBy={sortBy} isLoading={isLoading} />
        </Button>
      </BottomSheetTrigger>
      <BottomSheetContent
        title="Sort Salaries"
        onCloseClick={() => {
          // reset back to the original sortBy
          setSortBy(sortBy)
        }}
      >
        {sortOptions.map((toggleOptionGroup) => {
          const activeOption =
            toggleOptionGroup.find((option) => activeSortBy === option.value) ||
            toggleOptionGroup[0]
          const { Icon, value, label } = activeOption
          return (
            <SelectItem
              key={value}
              isActive={activeSortBy === value}
              onClick={() => handleItemClick(value)}
              className="justify-between w-full text-md py-3"
            >
              {label}{' '}
              {activeSortBy === activeOption.value ? (
                <Icon className="w-6 h-6" />
              ) : null}
            </SelectItem>
          )
        })}
        <Button
          size="lg"
          onClick={() => {
            setSortBy(activeSortBy)
            setOpen(false)
          }}
          className="mt-4 w-full rounded-full bg-button-primary-bg hover:bg-button-primary-hover-bg"
        >
          Apply
        </Button>
      </BottomSheetContent>
    </BottomSheet>
  )
}

export function SortRow({
  isAuthenticated,
  countPublicInteraction,
  hasTrendsResults,
}: {
  isAuthenticated: boolean
  countPublicInteraction: () => void
  hasTrendsResults: boolean
}) {
  const hasInitRef = useRef(false)
  const wasIsLoadingTrueRef = useRef(false)
  const isMobile = useIsMobile(900)
  const posthog = usePostHog()
  const { salaryFilterControls, updateSalaryFilters, loadingResults } =
    useSalaryFilters({
      isAuthenticated,
      countPublicInteraction,
    })
  const isLoading = loadingResults === LoadingResults.SortBy
  const [sortBy, setSortBy] = useState<SortOption | null>(
    (salaryFilterControls?.sortBy as SortOption) || SortOption.postedAtDesc,
  )

  useEffect(() => {
    if (isLoading) {
      wasIsLoadingTrueRef.current = true
    }
    if (wasIsLoadingTrueRef.current && !isLoading && !hasInitRef.current) {
      hasInitRef.current = true
      if (salaryFilterControls?.sortBy) {
        setSortBy(salaryFilterControls?.sortBy as SortOption)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading])

  const { data } = useSalaryHistogram()
  const { salaryCount } = data?.getHistogram || {}
  const formattedSalaryCount = getFormattedSalaryCount(salaryCount)

  const updateSortBy = (option: SortOption | null) => {
    setSortBy(option)
    updateSalaryFilters(
      { sortBy: option },
      { loadingResult: LoadingResults.SortBy },
    )
    posthog.capture('selected_sort', {
      product_area: 'salaries',
      module_name: 'main',
      sort_by: option,
    })
  }

  const hasSalaryCount = (salaryCount ?? 0) > 1
  const hasTrends =
    isAuthenticated &&
    hasTrendsResults &&
    hasTrendsTab(salaryFilterControls?.jobType, salaryFilterControls?.state)

  return (
    <div className="flex justify-between items-center py-5 md:py-3 pr-4">
      {hasSalaryCount ? (
        hasTrends ? (
          <SalaryTabs salaryCount={formattedSalaryCount} />
        ) : (
          <span className="text-sm text-copy-accent ml-5">{`${formattedSalaryCount} Salaries`}</span>
        )
      ) : null}

      {isMobile ? (
        <SortRowMobile
          sortBy={sortBy}
          setSortBy={updateSortBy}
          isLoading={isLoading}
        />
      ) : (
        <SortRowDesktop
          sortBy={sortBy}
          setSortBy={updateSortBy}
          isLoading={isLoading}
        />
      )}
    </div>
  )
}
